import * as React from 'react'
import { forwardRef, ReactElement, useMemo } from 'react'
import { Button, ButtonProps, Tooltip } from '@fluentui/react-components'
import useViewport, { Viewport } from '../../../hooks/useViewport'
import { RichText } from '../Text/RichText'

/**
 * Controls how the button renders across viewports:
 * - 'responsive' (default): icon on a small viewport, text on anything larger.
 * - 'iconOnly': always icon, never text.
 * - 'iconAndLabel': always icon, plus text on anything larger than a small viewport.
 */
export type TooltippedButtonDisplay = 'responsive' | 'iconOnly' | 'iconAndLabel'

export type TooltippedButtonProps = Omit<ButtonProps, 'children'> & {
    /** Text shown on larger viewports and used as the tooltip when the text is hidden. */
    label?: string
    /** Explicit tooltip content; shown on every viewport because it says more than the label does. */
    tooltip?: string
    /** Renders an explicit tooltip as markdown through `RichText` instead of as plain text. */
    richTooltip?: boolean
    display?: TooltippedButtonDisplay
}

/**
 * A Button that only shows a tooltip when it actually adds something: when the label is hidden
 * (small viewport or icon-only), or when an explicit tooltip carries extra information.
 */
const TooltippedButton = forwardRef<HTMLButtonElement | HTMLAnchorElement, TooltippedButtonProps>(function TooltippedButton(
    { label, tooltip, richTooltip, display = 'responsive', icon, ...buttonProps },
    ref,
): ReactElement {
    const { isViewport, viewport } = useViewport()

    const viewportSmall = useMemo<boolean>(() => isViewport('<=', Viewport.OutlookNew), [viewport])

    const showLabel = display !== 'iconOnly' && !viewportSmall
    const showIcon = display !== 'responsive' || viewportSmall

    const button = (
        <Button {...(buttonProps as ButtonProps)} ref={ref} icon={showIcon ? icon : undefined}>
            {showLabel ? label : undefined}
        </Button>
    )

    // A tooltip or label of only whitespace says as little as an empty one, so it counts as absent.
    const explicitTooltip = tooltip?.trim() ? tooltip : undefined
    const labelAsTooltip = !showLabel && !explicitTooltip && label?.trim() ? label : undefined

    const content = explicitTooltip ?? labelAsTooltip
    if (!content) {
        return button
    }

    // Only an explicit tooltip is rendered as markdown: a label standing in for the hidden button
    // text is the accessible name, which should stay a plain string.
    // RichText mounts a tiptap editor, so it is only created once the tooltip has content to render.
    const tooltipContent = richTooltip && explicitTooltip ? <RichText content={explicitTooltip} /> : content

    return (
        <Tooltip content={tooltipContent} relationship={showLabel ? 'description' : 'label'} withArrow>
            {button}
        </Tooltip>
    )
})

export default TooltippedButton
